import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, OnDestroy, OnInit, Output, ViewChild, ViewContainerRef } from '@angular/core';
import { DialogModalSize, DialogRef } from '../../../dialog/dialog-ref';
import { AppService } from 'src/modules/base/shared/services/app.service';
import { UtilService } from 'src/modules/base/services/util.service';
import { AlertsService } from 'src/modules/base/services/alerts.service';
import { DialogConfig } from '../../../dialog/dialog-config';
import { BaseService } from 'src/modules/base/shared/services/base.service';
import { AIService } from '../../services/ai.service';
import { LoaderService } from 'src/modules/base/services/loader.service';
import { ActivatedRoute } from '@angular/router';
import { DialogService } from '../../../dialog/dialog.service';
import { HttpHeaders } from '@angular/common/http';
import { TranscriptionData, TranscriptionService } from './transcription.service';
import { Subscription } from 'rxjs/internal/Subscription';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';
import { Subject } from 'rxjs/internal/Subject';
// import { any } from 'cypress/types/bluebird';

declare var window: any;

@Component({
    selector: 'app-kx-ai-speech',
    templateUrl: './kx-ai-speech.component.html',
    styleUrls: ['./kx-ai-speech.component.scss'],
    //changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [TranscriptionService],
    standalone: false
})
export class KxAISpeechComponent implements OnInit, OnDestroy {
  // ====== Tab Management ======
  @Output() getData = new Subject<any>();

  tabs = [
    { key: 'voice', label: 'AI Voice' },
    { key: 'scan', label: 'AI Scan' }
  ];
  activeTab: 'voice' | 'scan' = 'voice';
  showVoiceTab = true;
  showScanTab = false;
  hasPermission: boolean | null = null;
  // Subscriptions
  private subscriptions = new Subscription();
  private durationInterval?: number;

  
  interimTranscript = '';
  fullTranscript = '';
  documentId = '';
  public audioDocumentId = '';
  isTrascriptionCollapsed:boolean=false
  config: any = {voiceServiceMessage:'',voiceServiceAvailable:true,lastResultTimestamp:0,lastSentOffset:0,previousResponses:[],transcriptionTimer:null, intervalTranscription:60,continousTranscription: false,manualMaximized:false,minimized:false,duration:0,wordCount:0,autoScroll:true,isManualStop:false,baseRetryDelay:1000,lastAudioTimestamp:0,maxRetries:5,retryCount:0,healthCheckInterval:null,showMicroPhonePopUp:false,isTranscriptionExpanded:false,isBackendVoiceAnalysisEnabled:false,currentVoiceModeIsBackend:false, isListening: false,loading:false,decorator:false,async_task_id:false,formConfig:{},processingStatus:'Gathering data...',statusInterval:null,progressSteps:[],progressPercent:0,progressKnown:false,liveProgress:false,statusMessages:[
    'Reading recorded / scanned data',
    'Analyzing clinical context',
    'Searching masters',
    'Matching master records',
    'Mapping chief complaints',
    'Mapping allergies',
    'Mapping medications',
    'Mapping clinical data',
    'Validating fields',
    'Finalizing form data'
  ],
  //Plain cycling text used when the step-timeline UI (show_progress_bar_with_percentage) is off.
  oldStatusMessages:[
    'Gathering transcribed data...',
    'Analyzing clinical context...',
    'Extracting medical entities...',
    'Structuring form data...',
    'Validating fields...',
    'Finalizing clinical summary...',
    'Almost there, Hang Tight...'
  ] };
  isBrowserSupported = true;
  // Rotating status copy shown while recording, so the recording-only UI
  // feels alive instead of a blank panel (no live transcript to display).
  public readonly recordingStatusMessages: string[] = [
    'Recording your voice…',
    'Capturing audio…',
    'Listening closely…',
    'Still recording…',
    'Your voice is being saved…'
  ];
  get recordingStatusText(): string {
    if (!this.config.isListening) return '';
    const idx = Math.floor(this.config.duration / 3) % this.recordingStatusMessages.length;
    return this.recordingStatusMessages[idx];
  }
  private mediaStream: MediaStream | null = null;
  private recognition: any;
  private restartTimeout: any = null;
  private SpeechRecognition: any;
  private audioContext: any;
  public parentConfig:any={};
  private _isAutoReconnecting = false;
  public hasBrowserSpeechRecognition = false;
  private _llmInFlight = false;
  private _llmPending = false;
  private _audioReadyPromise: Promise<void> | null = null;
  /**
   * Facility config EMR / upload_document_voice. "yes" (the config's default)
   * uploads the recording to DMS (S3/Azure); "no" sends it inline to the LLM
   * and never stores it. A failed lookup resolves false so a facility that
   * opted out never gets a stored recording by accident.
   */
  private _saveVoiceRecording: Promise<boolean> = Promise.resolve(true);
  private static readonly SAVE_VOICE_CONFIG = 'module_code:emr,config_code:upload_document_voice';
  /** Sample rate of the inline (unsaved) WAV — enough for speech, ~1/3 of a 48kHz recording. */
  private static readonly INLINE_AUDIO_SAMPLE_RATE = 16000;

  private _llmQueue: string[] = [];
  private readonly MIN_WORDS_THRESHOLD = 20;
  private readonly MAX_CHUNK_CHARS = 6000; 
  private readonly LARGE_TRANSCRIPT_ASYNC_THRESHOLD = 6000;
  private readonly AI_FILL_TIMEOUT_MS = 900000;
  private readonly LLM_MAX_RETRIES = 3;
  private readonly LLM_BASE_DELAY = 1000; // ms
  private _restartAttempts = 0;
  private readonly MAX_RESTART_ATTEMPTS = 5;
  private _processedDisplayLength = 0;
  public processedDisplay = '';

  // Continuous audio capture (MediaRecorder — never stops, never loses data)
  private mediaRecorder: MediaRecorder | null = null;
  private mediaRecorderMimeType = 'audio/webm';
  private audioChunks: Blob[] = [];
  public recordedBlob: Blob | null = null;
  public audioUrl: string | null = null;
  // True once the user has paused an in-progress recording (mic clicked while
  // listening) so the record button can show a Pause icon instead of
  // fa-microphone-slash. Set synchronously in startListening()/stopListening()
  // rather than derived from recordedBlob, since recordedBlob is only
  // populated after the async WAV conversion in buildAudioUrl() finishes.
  public isPaused: boolean = false;
  // Accurate cumulative duration tracking across multiple start/stop cycles (ms)
  private recordingSessionStartMs = 0;
  private totalRecordedMs = 0;

  // ── Max recording limit ──────────────────────────────────────────────────
  /** Maximum cumulative actual-recording seconds allowed per session (default 10 min). */
  private readonly MAX_RECORDING_SECONDS = 600;
  /** Set to true once the limit is reached; blocks further recording until cleared. */
  public maxRecordingReached = false;

  // Predictable STT rotation — we kill it every 25 s ourselves so nothing is lost
  private sttRestartInterval: any = null;
  private readonly STT_ROTATION_MS = 25000;

  private audioSourceNode: MediaStreamAudioSourceNode | null = null;
private analyserNode: AnalyserNode | null = null;
private analyserPollTimer: any = null;
private readonly AUDIO_ENERGY_THRESHOLD = 0.0008; // tune this
private readonly AUDIO_INACTIVITY_THRESHOLD_MS = 15000; // 15s default
public selectedLang = 'en-IN'; // default, can be changed by user
public availableLangs = ['en-US','en-GB','en-IN','hi-IN','es-ES','es-MX','fr-FR']; 

private watchdogInterval: any = null;
private micLevel: number = 0;       
private _forceImmediateRestart = false;

// Watchdog thresholds (tweakable)
private readonly WATCHDOG_NO_RESULT_MS = 5000;        // no result for 5s
private readonly WATCHDOG_AUDIO_ACTIVE_MS = 2000;     // mic was active within 2s
private readonly WATCHDOG_CHECK_INTERVAL_MS = 1000;   // check every 1s

  @ViewChild('customComponent', { read: ViewContainerRef }) customComponent: ViewContainerRef;
  @ViewChild('uiEngineComponent', { read: ViewContainerRef }) uiEngineComponent: ViewContainerRef;
  constructor(
    private dialogRef: DialogRef,
    private appService: AppService,
    public util: UtilService,
    private alert: AlertsService,
    private baseService: BaseService,
    private loader: LoaderService,
    private aiService: AIService,
    private alertService: AlertsService,
    public dialogConfig: DialogConfig,
    public dialogService: DialogService,
    public route: ActivatedRoute,
    private transcriptionService: TranscriptionService,
    private cdr:ChangeDetectorRef,
    public injector: Injector,
  ) {
    
    this.initializeSpeechAPI();
    this.loadTabConfiguration();
  }

  //Read live from dialogConfig - v1-prescription.component.ts sets this flag inside an async
  //getResourceDef() callback, which may still be unresolved when this component is first
  //created, so a one-time read (e.g. in ngOnInit) could catch a stale value.
  protected showProgressBarEnabled(): boolean {
    return this.dialogConfig?.data?.formConfig?.show_progress_bar_with_percentage
      ?? this.dialogConfig?.config?.resource_config?.ai?.form?.show_progress_bar_with_percentage
      ?? false;
  }

  // Step timeline shown while AI fill is running. Until the backend reports its own
  // progress (see updateLiveProgress) the step labels advance on a timer, but no
  // percentage is shown: a timer-driven % has nothing to do with how far the fill
  // really is (it used to complete at 20% or 50%). The bar stays indeterminate.
  private startStatusRotation() {
    if (!this.showProgressBarEnabled()) {
      //Plain cycling text only - no step timeline, percent, or live progress.
      let index = 0;
      this.config.processingStatus = this.config.oldStatusMessages[0];
      this.config.statusInterval = setInterval(() => {
        index = (index + 1) % this.config.oldStatusMessages.length;
        this.config.processingStatus = this.config.oldStatusMessages[index];
        this.cdr.detectChanges();
      }, 4000); // Change text every 4 seconds
      return;
    }
    //Step timeline + live backend progress, shown only when the flag is on.
    this.resetProgress();
    const customSteps = this.dialogConfig?.data?.formConfig?.progress_steps;
    const steps: string[] = (Array.isArray(customSteps) && customSteps.length) ? customSteps : this.config.statusMessages;
    this.pushProgressStep(steps[0]);
    let index = 0;
    this.config.statusInterval = setInterval(() => {
      if (this.config.liveProgress) {
        return;
      }
      //Loops indefinitely instead of freezing on the last step, for forms whose backend never
      //calls updateLiveProgress() with real progress (e.g. the generic AIGenericForm/form_filling
      //handler), so it doesn't look stuck for the whole wait.
      index = (index + 1) % steps.length;
      this.pushProgressStep(steps[index]);
      this.cdr.detectChanges();
    }, 4000); // Advance a step every 4 seconds
  }

  // Clear the previous run's timeline/percent. Called as soon as Done is clicked, because
  // the panel becomes visible (config.loading) while audio/schema are still being prepared,
  // and it would otherwise show the last run's steps and 100% for those few seconds.
  private resetProgress(initialStep?: string) {
    if (this.config.statusInterval) {
      clearInterval(this.config.statusInterval);
      this.config.statusInterval = null;
    }
    this.config.liveProgress = false;
    this.config.progressKnown = false;
    this.config.progressSteps = [];
    this.config.progressPercent = 0;
    if (initialStep) {
      this.pushProgressStep(initialStep);
    }
  }

  private pushProgressStep(label: string) {
    if (!label) return;
    const last = this.config.progressSteps[this.config.progressSteps.length - 1];
    if (last && last.label === label) return;
    if (last) last.state = 'done';
    this.config.progressSteps.push({ label, state: 'active' });
    this.config.processingStatus = label;
  }

  // ai_async_tasks poll result: if the backend writes progress_message / progress on the
  // task, switch from the simulated timeline to what is really happening.
  private updateLiveProgress(asyncRes: any) {
    if (!asyncRes) return;
    const message = asyncRes.progress_message || asyncRes.current_step;
    if (message) {
      if (!this.config.liveProgress) {
        this.config.liveProgress = true;
        this.config.progressSteps = [];
        if (this.config.statusInterval) {
          clearInterval(this.config.statusInterval);
          this.config.statusInterval = null;
        }
      }
      this.pushProgressStep(message);
    } else if (asyncRes.status === 'IN-QUEUE' && !this.config.liveProgress) {
      this.config.processingStatus = 'Waiting in queue...';
    }
    // Only the backend's number is shown, and it never moves backwards between polls
    const percent = Number(asyncRes.progress);
    if (!isNaN(percent) && percent > 0) {
      this.config.progressKnown = true;
      this.config.progressPercent = Math.max(this.config.progressPercent, Math.min(99, percent));
    }
    this.cdr.detectChanges();
  }

  private stopStatusRotation(completed = false) {
    if (this.config.statusInterval) {
      clearInterval(this.config.statusInterval);
      this.config.statusInterval = null;
    }
    if (completed) {
      this.config.progressSteps.forEach(step => step.state = 'done');
      this.config.progressPercent = 100;
      this.config.progressKnown = true;
    }
    this.cdr.detectChanges();
  }

  ngOnInit(): void {
    if(this.dialogConfig?.data?.formConfig?.feature != undefined && Array.isArray(this.dialogConfig?.data?.formConfig?.feature ) && this.dialogConfig?.data?.formConfig?.feature.indexOf(AIService.VOICE_FILL)>-1){
      this.checkMicroPhone$();
    }
    this._saveVoiceRecording = this.loadSaveVoiceRecordingConfig();

    if(this.dialogConfig?.data?.formConfig?.minimized != undefined){
      this.config.minimized = this.dialogConfig?.data?.formConfig?.minimized;
    }
    if(this.dialogConfig?.data?.formConfig?.continous_transcription != undefined){
      this.config.continousTranscription = this.dialogConfig?.data?.formConfig?.continous_transcription;
    }

    if(this.dialogConfig?.data?.formConfig?.interval_transcription != undefined){
      this.config.intervalTranscription = this.dialogConfig?.data?.formConfig?.interval_transcription;
    }
    
    if(this.dialogConfig?.data?.formConfig?.decorator != undefined && this.aiService.AI_DECORATOR_DEF[this.dialogConfig?.data?.formConfig?.decorator] != undefined){
      this.config.decorator = this.aiService.initDecorator(this.dialogConfig?.data?.formConfig?.decorator, this.route, this.injector);
    }

    if(this.appService.getAccountConfig()["ai_transcription"]){
      this.config.isBackendVoiceAnalysisEnabled = true;
    }
    this.determineVoiceServiceAvailability();
  }

   /**
   * Determines whether the voice feature is available in the current environment.
   * Recording is always browser-based (MediaRecorder + SpeechRecognition).
   * `currentVoiceModeIsBackend` is now only a preference flag that controls whether
   * the audio blob is forwarded to the LLM on Done.
   */
   private determineVoiceServiceAvailability(): void {
    const hasMediaRecorder = typeof MediaRecorder !== 'undefined';
    const hasGetUserMedia = !!(navigator.mediaDevices?.getUserMedia);
    if (!hasMediaRecorder || !hasGetUserMedia) {
        // Neither live transcription nor audio recording is available
        // (e.g. restricted WebView)
        this.config.voiceServiceAvailable = false;
        this.config.voiceServiceMessage = 'Audio recording is not supported in this browser or environment.';
    } else if (this.hasBrowserSpeechRecognition) {
        // Chrome/Edge: live transcription + optional AI mode
        this.config.voiceServiceAvailable = true;
        this.config.currentVoiceModeIsBackend = this.config.isBackendVoiceAnalysisEnabled;
    } else {
        // Firefox, mobile WebViews, etc.: no live transcription but MediaRecorder works.
        // Force backend AI mode — audio blob is sent to the LLM on Done.
        // The toggle is hidden in the UI so the user cannot turn it off.
        this.config.voiceServiceAvailable = true;
        this.config.currentVoiceModeIsBackend = true;
    }
  }
  changeSpeechContainerSizeBasedOnPopupAndFloatingHost() {
    let floatingHost = document.querySelector(".kx-floating-host") as HTMLElement
    let speechContainer = document.getElementsByClassName("ai-speech-card-container")[0] as HTMLElement
    if (!speechContainer) return;
    if (floatingHost) {
     // speechContainer.style.maxWidth = "500px";
     if(!speechContainer.classList.contains('ai-speech-card-container-floating'))
     speechContainer.classList.add("ai-speech-card-container-floating")
    }
    else {
      //speechContainer.style.maxWidth = "100%";
      if(!speechContainer.classList.contains('ai-speech-card-container-popup'))
        speechContainer.classList.add("ai-speech-card-container-popup")
      //speechContainer.classList.add("ai-speech-card-container-popup")
    }
  }
  minimize()
  {
    let compress = document.getElementsByClassName("fa-compress")[0] as HTMLElement;
    let header = document.getElementsByClassName("ai-title-top")[0] as HTMLElement;
    document.getElementById("aiFloatingHeading").style.display = "unset"
    compress.style.color = "unset";
    if (header) {
      header.style.color = "unset"
    }
    let expandStyle = document.querySelector('.kx-floating-host app-kx-ai-speech ._speech-container .single-btn-container button.collapse-toggle ') as HTMLElement
    if (expandStyle) {
      expandStyle.style.textAlign = "";
      expandStyle.style.color = "";
      expandStyle.style.background = "";
      expandStyle.style.position = "";
      expandStyle.style.top = "";
      expandStyle.style.right = "";

    }
    let floatingHost = document.getElementsByClassName("kx-floating-host")[0] as HTMLElement;
    floatingHost.style.flexDirection = "row";
    floatingHost.style.alignItems = "center"
    let floatingDragHandle = document.getElementsByClassName("kx-floating-drag-handle")[0] as HTMLElement;
    floatingDragHandle.style.width = "35px"
    floatingDragHandle.style.marginTop = "0"
    floatingDragHandle.style.position = "unset"
    floatingDragHandle.style.color = "black"
    floatingDragHandle.style.right = "unset"
  }
  toggleTranscription(isArrowCollaspe?): void {
    this.changeSpeechContainerSizeBasedOnPopupAndFloatingHost()
    if (!isArrowCollaspe) {
      this.isTrascriptionCollapsed = false;
      this.config.isTranscriptionExpanded = !this.config.isTranscriptionExpanded;
      let compress = document.getElementsByClassName("fa-compress")[0] as HTMLElement;
      let header = document.getElementsByClassName("ai-title-top")[0] as HTMLElement;
      if (this.config.isTranscriptionExpanded) {
        document.getElementById("aiFloatingHeading").style.display = "block"
        document.getElementById("aiFloatingHeading").style.marginBottom = "10px";
        let ele = document.getElementsByClassName("fa-compress")[0]
        let expandStyle = document.querySelector('.kx-floating-host app-kx-ai-speech ._speech-container .single-btn-container button.collapse-toggle ') as HTMLElement
        if (expandStyle) {
          expandStyle.style.background = "trasparent";
          expandStyle.style.position = "absolute";
          expandStyle.style.top = "5px";
          expandStyle.style.right = "40px";
          expandStyle.style.left = "unset";
          expandStyle.style.color = "black";
          expandStyle.style.borderRadius = "5px";
        }
        let floatingHost = document.getElementsByClassName("kx-floating-host")[0] as HTMLElement;
        floatingHost.style.flexDirection = "column";
        floatingHost.style.alignItems = "unset"
        let floatingDragHandle = document.getElementsByClassName("kx-floating-drag-handle")[0] as HTMLElement;
        floatingDragHandle.style.width = "32px"
        floatingDragHandle.style.width = "32px"
        floatingDragHandle.style.position = "absolute"
        floatingDragHandle.style.right = "0"
        floatingDragHandle.style.marginTop = "5px"
      }
      else {
        this.minimize();
      }
    }
    else {
      //this.config.isTranscriptionExpanded=!this.config.isTranscriptionExpanded;
      document.getElementById("transcription-panel").classList.toggle('expanded')
      this.isTrascriptionCollapsed = !this.isTrascriptionCollapsed;
    }
    
  }

  // Load which tabs to show from configuration
  private loadTabConfiguration() {
    if(this.dialogConfig?.data != undefined){
      try {
        this.showVoiceTab = this.dialogConfig?.data.voiceFill ?? false;
        this.showScanTab = this.dialogConfig?.data.scanFill ?? true;
        if (!this.showVoiceTab && this.showScanTab) {
            this.activeTab = 'scan';
            this.createScanTab()
        }
        
      } catch (e) {
        console.warn('Failed to load AI feature configuration', e);
      }
    }    
  }

  createScanTab(){
    if(this.activeTab == 'scan'){
      var data = this.dialogConfig?.data
      var formConfig = JSON.parse(JSON.stringify(data.formConfig))
      const full_prompt_code = formConfig.prompt_code+"_"+AIService.SCAN_FILL

      setTimeout(()=>{
        var input:any = {}
        input.resource = 'ai_scan'
        input.maxSize = 15
        input.crop_image = false
        this.createComponent(this.customComponent,'dhs-file-uploader',input,(compObj)=>{
          this.config.uploadObj = compObj
        });

        var inputUi:any = {}
        inputUi.resourcename = 'ai_async_tasks'
        inputUi.current_action = 'LIST'
        inputUi.showGridOnly = true
        inputUi.parentForm = this.dialogConfig.config
        inputUi.filter = 'prompt_code:'+full_prompt_code+",user_id:"+this.baseService.authenticationService.getCurrentUser().user_id
        this.createComponent(this.uiEngineComponent,'app-ui-engine',inputUi,(compObj)=>{
          this.config.uiEngineScanObj = compObj
          compObj.dialogRef = null//prevents auto closing the popup
        });
      })
    }
  }

  selectTab(key: 'voice' | 'scan') {
    this.activeTab = key;
    this.createScanTab()
  }

  // ====== Speech Initialization ======
  private initializeSpeechAPI() {
    try {
      // ====== Voice-to-text (live browser speech recognition) is DISABLED ======
      // Product decision: this component is recording-only now. Audio capture
      // (MediaRecorder) and the live mic-level waveform still work as before;
      // we simply never create/start a SpeechRecognition instance, so the
      // component always follows the existing "no live transcription" path
      // (previously used only for Firefox/mobile). To re-enable voice-to-text,
      // restore the detection below.
      //
      // this.SpeechRecognition =
      //   window.SpeechRecognition ||
      //   window.webkitSpeechRecognition ||
      //   window.mozSpeechRecognition ||
      //   window.msSpeechRecognition;
      //
      // // Disable STT on mobile — browsers play an audible beep on every start/stop
      // // which is disruptive. Audio-only recording (MediaRecorder) is used instead.
      // this.hasBrowserSpeechRecognition = !!this.SpeechRecognition && !this.util.isMobile();
      this.hasBrowserSpeechRecognition = false;

      this.isBrowserSupported = true;
      this.config.currentVoiceModeIsBackend = true;
      // Audio-context autoplay/resume handling is still required for the mic-level
      // analyser and WAV encoding, independent of speech recognition, so keep it.
      this.handleBrowserSpecificIssues();
    } catch (err) {
      console.error('initializeSpeechAPI error', err);
      this.isBrowserSupported = false;
      this.hasBrowserSpeechRecognition = false;
      this.config.currentVoiceModeIsBackend = false
      this.alert.error('Unable to initialize speech recognition');
    }
  }

  private handleBrowserSpecificIssues() {
    if (this.util.isSafari()) {
      this.handleSafariRequirements();
    } else if (this.util.isChrome()) {
      this.handleChromeAutoplayPolicy();
    }
  }

  private handleSafariRequirements() {
    document.addEventListener(
      'click',
      this.initSafariRecognition.bind(this),
      { once: true }
    );
  }

  private initSafariRecognition() {
    if (this.util.isSafari() && !this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
  }

  private handleChromeAutoplayPolicy() {
    if (window.AudioContext || window.webkitAudioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      if (this.audioContext.state === 'suspended') {
        document.addEventListener('click', this.resumeAudioContext.bind(this), { once: true });
      }
    }
  }

  private resumeAudioContext() {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      this.audioContext.resume().then(() => {
        console.log('Audio context resumed successfully');
      });
    }
  }

  // ====================================================================
  // The methods below (recognition creation, watchdog, health monitoring,
  // speech-result handling, grammar rules, restart/rotation logic) implement
  // the browser Web Speech API live-transcription pipeline. Voice-to-text is
  // currently DISABLED (see initializeSpeechAPI — hasBrowserSpeechRecognition
  // is forced to false), so none of this is ever invoked. Left in place only
  // so the feature can be restored later by re-enabling the flag.
  // ====================================================================

  // ====== Recognition Setup ======
  private createRecognition() {
    const recognition = new this.SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = this.selectedLang || 'en-IN';
    recognition.maxAlternatives = 1;

    if (this.util.isSafari()) {
      recognition.continuous = false;
      recognition.interimResults = false;
    }

    recognition.isRunning = false;
    const originalStart = recognition.start.bind(recognition);
    recognition.start = () => {
      originalStart();
      recognition.isRunning = true;
    };

    const originalStop = recognition.stop.bind(recognition);
    recognition.stop = () => {
      originalStop();
      recognition.isRunning = false;
    };

    recognition.onresult = (event: any) => this.handleSpeechResult(event);
    recognition.onerror = (error: any) => {
      console.error('recognition.onerror', error);
      this.handleRecognitionError(error);
    }
    recognition.onstart = () => {
      // seed timestamps
      this.config.lastResultTimestamp = Date.now();
      // start the watchdog loop (idempotent)
      this.startRecognitionWatchdog();
    };
    recognition.onend = () => {
      this.stopRecognitionWatchdog();
      console.warn('recognition.onend', { isListening:this.config.isListening, manual:this.config.isManualStop });
      this.handleRecognitionEnd();
    }
    recognition.onaudiostart = () => { this.config.lastAudioTimestamp = Date.now(); };
    recognition.onspeechstart = () => { this.config.lastAudioTimestamp = Date.now(); };
    recognition.onspeechend = () => {
      // speech ended — update timestamp so analyzer / health check know we saw voice recently
      this.config.lastAudioTimestamp = Date.now();
      // do not automatically stop; let onend handle restart if needed
    };
    return recognition;
  }

  private startRecognitionWatchdog() {
    // clear any existing watchdog to avoid doubles
    this.stopRecognitionWatchdog();
  
    this.watchdogInterval = setInterval(() => {
      try {
        const now = Date.now();
        const noResultFor = now - (this.config.lastResultTimestamp || 0);
        const lastAudioAgo = now - (this.config.lastAudioTimestamp || 0);
  
        // Condition: audio recently active but no transcription results for a while
        if (noResultFor > this.WATCHDOG_NO_RESULT_MS && lastAudioAgo < this.WATCHDOG_AUDIO_ACTIVE_MS) {
          console.warn(`Watchdog: no result for ${noResultFor}ms but audio was active ${lastAudioAgo}ms ago — forcing immediate restart`);
          // Set flag so restartRecognition will avoid long exponential backoff
          this._forceImmediateRestart = true;
          this.restartRecognition();
        }
      } catch (e) {
        console.warn('Watchdog check failed', e);
      }
    }, this.WATCHDOG_CHECK_INTERVAL_MS);
  }
  
  private stopRecognitionWatchdog() {
    if (this.watchdogInterval) {
      clearInterval(this.watchdogInterval);
      this.watchdogInterval = null;
    }
  }
  



  // ====== Health Monitoring ======
  private startHealthMonitoring() {
    this.config.healthCheckInterval = setInterval(() => this.checkSystemHealth(), 2000);
  }
  private stopHealthMonitoring() {
    if (this.config.healthCheckInterval) {
      clearInterval(this.config.healthCheckInterval);
      this.config.healthCheckInterval = null;
    }
  }

  private checkSystemHealth() {
    if (!this.config.isListening || this.config.isManualStop) return;
    if (document.hidden) return; // avoid restarts while tab hidden
    
    const isRecognitionActive = !!(this.recognition?.isRunning);
    const tracks = this.mediaStream?.getAudioTracks() || [];
    const isMicActive = tracks.length > 0 && tracks.some(t => t.readyState === 'live' && t.enabled);
    const timeSinceLastAudio = Date.now() - (this.config.lastAudioTimestamp || 0);
    const VERY_SHORT_GAP_MS = 2500;
    if (timeSinceLastAudio < VERY_SHORT_GAP_MS) {
      // brief pause — keep running
      return;
    }
    // If the recognition object or mic tracks are dead, restart immediately
    if (!isRecognitionActive || !isMicActive) {
      console.warn('Health check: recognitionActive=', isRecognitionActive, 'micActive=', isMicActive, ' — restarting recognition');
      this.restartRecognition();
      return;
    }
  
    // If audio energy hasn't been seen for a longer threshold, restart
    if (timeSinceLastAudio > this.AUDIO_INACTIVITY_THRESHOLD_MS) {
      console.warn('Audio pipeline idle for', timeSinceLastAudio, 'ms — restarting recognition');
      this.restartRecognition();
    }
  
    // If audioContext is suspended, try to resume
    if (this.audioContext && this.audioContext.state === 'suspended') {
      try {
        this.audioContext.resume();
        console.log('Resumed audioContext from health check');
      } catch (e) {
        console.warn('audioContext.resume() failed', e);
      }
    }
  }

  // ====== Event Handlers ======
  private handleSpeechResult(event: any) {
    this.config.lastAudioTimestamp = Date.now();
    this.config.lastResultTimestamp = Date.now();
    for (let i = event.resultIndex; i < event.results.length; ++i) {
      const transcript = event.results[i][0].transcript;
      this.updateTranscript(transcript, event.results[i].isFinal);
    }
    this.updateDisplayText();
  }

  private updateDisplayText() {
    const full = (this.fullTranscript || '') + (this.interimTranscript || '');
    // Get only new tail since last processed
    const newPart = full.substring(this._processedDisplayLength);
    if (newPart) {
      this.processedDisplay += this.applyGrammarRules(newPart);
      this._processedDisplayLength = full.length;
    }
    // prefer template binding; fallback to DOM write if necessary
    // const outputElement = document.getElementById('speech-output');
    // if (outputElement) outputElement.textContent = this.processedDisplay;
    this.updateRecordingUI();
  }
  

  private applyGrammarRules(text: string): string {
    return Object.entries(this.getGrammarReplacements())
      .reduce((str, [key, val]) => str.replace(new RegExp(key, 'gi'), val), text);
  }

  private getGrammarReplacements() {
    return {
      underscore: '_',
      'forward slash': '/',
      hyphen: '-',
      colon: ':',
      semicolon: ';',
      dot: '.',
      'question mark': '?',
      'new line': '\n',
      'new paragraph': '\n\n'
    };
  }

  private handleRecognitionError(error: any) {
    console.error('Recognition error:', error);

    // 'aborted' is fired by the browser whenever WE call recognition.stop() (during rotation
    // or safeStopRecognition). It is expected and harmless — the companion 'onend' event will
    // fire immediately after and do the right thing. Never call stopListening() for aborted.
    if (error.error === 'aborted' || error.error === 'no-speech') return;

    // if (error.error === 'network') {
    //   this.config.retryCount++;
    //   if (this.config.retryCount <= this.config.maxRetries) {
    //     this.alert.alert(`Network issue... Retrying (${this.config.retryCount}/${this.config.maxRetries})`);
    //     this.restartRecognition();
    //     return;
    //   }
    // }
    this.stopListening();
    if (this.config.retryCount > this.config.maxRetries) {
      this.alert.error('Connection lost. Please check network and retry.');
      this.config.retryCount = 0;
    }
  }

  private async handleRecognitionEnd() {
    // If user manually stopped listening, do not auto restart
    if (!this.config.isListening || this.config.isManualStop) return;
  
    console.warn('Recognition ended. Attempting to recover...');
  
    // If Safari, prefer a tiny delayed restart (it often needs a short gap)
    if (this.util.isSafari()) {
      // small, conservative delay so we don't hit rapid loops
      setTimeout(() => {
        if (this.config.isListening && !this.config.isManualStop) {
          try { this.startListening(); } catch (e) { console.warn('Safari restart failed', e); }
        }
      }, 300);
      return;
    }
  
    // For non-Safari, attempt a robust resume:
    // 1) try resuming AudioContext (may be suspended by browser)
    try {
      if (this.audioContext && this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
        console.log('AudioContext resumed in onend handler');
      }
    } catch (e) {
      console.warn('audioContext.resume failed', e);
    }
  
    // 2) if media tracks are dead, try to re-acquire the media stream
    const tracks = this.mediaStream?.getAudioTracks() || [];
    const micLive = tracks.length > 0 && tracks.some(t => t.readyState === 'live' && t.enabled);
  
    if (!micLive) {
      const reacquired = await this.reAcquireMediaStream();
      if (!reacquired) {
        console.warn('Unable to reacquire media stream after recognition end');
        // let restartRecognition() apply backoff and cleanup
        this.restartRecognition();
        return;
      }
    }
  
    // 3) Finally, call restartRecognition which has backoff/attempt limits
    this.restartRecognition();
  }
  

  // ====== Control Methods ======
  checkMicroPhone$() {
    this.util.detectMicroPhone((hasMic: boolean) => {
      if (!hasMic) {
        this.alert.error('No microphone detected');
        return this.close();
      }
      this.checkAudioPermission((state: string) => {
        if (state === 'prompt' || state === 'denied') {
          this.config.showMicroPhonePopUp = true;
          this.util.checkAudioAccessStatus();
        } else {
          //this.startListening();
        }
      });
    });
  }

  /**
   * A single function to handle both starting and stopping the recording.
   * This is bound to our new single microphone button.
   */
  public toggleRecording(): void {
    if (this.config.isListening) {
      this.stopListening();
    } else {
      this.startListening();
    }
  }

  // ------------- MODE TOGGLE HANDLER -------------
  // The toggle now only records whether the user wants to send audio to the LLM (true)
  // or rely on the already-transcribed text (false). The state is forwarded to the API on Done.
  onModeToggle(useAudioForLLM: boolean): void {
    this.config.currentVoiceModeIsBackend = useAudioForLLM;
  }



  public async startListening() {
    console.log(this)
    if (this.maxRecordingReached) {
      this.alert.alert('Maximum recording time of 10 minutes reached. Press Done to submit or Clear to start over.');
      return;
    }
    if (this.config.isListening) return;
    if (this.config.isListening || !this.config.voiceServiceAvailable) {
      if (!this.config.voiceServiceAvailable) {
          console.warn("Start listening blocked: Voice service is not available.");
      }
      return;
    }
    this.config.isManualStop = false;
    this.config.isListening = true;
    this.isPaused = false;
    if (this.config.minimized) {
      this.config.manualMaximized = true;
      if (!this.config.isTranscriptionExpanded) {
        this.toggleTranscription();
      }
    }
    this.config.retryCount = 0;
    try {
      this.recordingSessionStartMs = Date.now();
      // Any new recording session invalidates the previously-uploaded audio document.
      this.audioDocumentId = '';
      // Reuse existing live stream so the MediaRecorder never changes container.
      // Only acquire a new stream on the very first start (or after a full clear/destroy).
      const streamAlive = this.mediaStream &&
        this.mediaStream.getAudioTracks().some(t => t.readyState === 'live');
      if (!streamAlive) {
        if (this.mediaStream) this.releaseMediaStream();
        // The stream was released on the previous manual stop (mic indicator fix).
        // The old paused recorder is bound to a dead stream — destroy it and start
        // fresh so the new recorder gets a valid live stream. audioChunks from the
        // previous session are cleared because the WAV is already in recordedBlob.
        if (this.mediaRecorder && this.mediaRecorder.state === 'paused') {
          this.destroyMediaRecorder();
          this.audioChunks = [];
        }
        this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        this.setupStreamTrackListeners(this.mediaStream);
      }
      this.attachAudioAnalyser(this.mediaStream);
      this.startMediaRecorder(this.mediaStream);   // resume if paused, start if new
      // ====== Voice-to-text (live transcription) is disabled — recording only ======
      // if (this.hasBrowserSpeechRecognition) {
      //   // Live transcription only available in Chrome/Edge; skip in Firefox and others
      //   this.recognition = this.createRecognition();
      //   this.recognition.start();
      //   this.startSTTRotation();                   // predictable 25 s rotation
      //   this.startHealthMonitoring();
      //   if(this.config.continousTranscription){
      //     this.startPeriodicProcessing()
      //   }
      // }
      this.startDurationTimer();
    } catch (error) {
      this.handleStartError(error);
    }

  }

  private handleStartError(error: any) {
    this.config.isListening = false;
    if (this.util.isFireFox() && error.message.includes('user gesture')) {
      this.alert.alert('Please click the mic button again to start listening');
    }
  }

  public stopListening() {
    this.finalizeCurrentTranscript();
    this.stopDurationTimer();
    this.stopSTTRotation();
    // Accumulate elapsed time for this session before pausing the recorder
    if (this.recordingSessionStartMs > 0) {
      this.totalRecordedMs += Date.now() - this.recordingSessionStartMs;
      this.recordingSessionStartMs = 0;
    }
    this.pauseMediaRecorder();  // flush chunk → pause
    if(this.config.continousTranscription){
      this.stopPeriodicProcessing()
    }
    this.config.isManualStop = true;
    this.config.isListening = false;
    this.isPaused = true;
    this.cleanupResources();    // stops STT first — must happen before releaseMediaStream
    this.releaseMediaStream();  // stop mic tracks AFTER recognition is dead so no stray onresult fires
  }

  private cleanupResources() {
    if (this.recognition) {
      // Silence onresult before stopping so any final result the browser fires
      // after stop() does not append to fullTranscript (which was already committed
      // by finalizeCurrentTranscript above).
      try { this.recognition.onresult = null; } catch (e) {}
      this.safeStopRecognition();
      this.recognition = null;
    }
    this.detachAudioAnalyser();
    this.stopHealthMonitoring();
    this.stopRecognitionWatchdog();
    if (this.restartTimeout) {
      clearTimeout(this.restartTimeout);
      this.restartTimeout = null;
    }
    if (this.config.healthCheckInterval) {
      clearInterval(this.config.healthCheckInterval);
      this.config.healthCheckInterval = null;
    }
    // NOTE: mediaStream is intentionally NOT stopped here.
    // It is kept alive so the single MediaRecorder can be resumed on next start.
    // Call releaseMediaStream() only from clear or destroy.
    this.updateRecordingUI();
  }

  /** Fully stops and releases the media stream. Call only on clear or destroy. */
  private releaseMediaStream(): void {
    if (!this.mediaStream) return;
    try {
      this.mediaStream.getAudioTracks().forEach(track => {
        try { track.onended = null; track.onmute = null; track.onunmute = null; } catch (e) {}
        try { track.stop(); } catch (e) {}
      });
    } catch (e) {}
    this.mediaStream = null;
  }

  barTransform(index: number): string {
    // stagger bars for nicer look: each bar reacts slightly differently
    const base = this.micLevel || 0;
    const stagger = 0.7 + index * 0.08;          // tweak for spread
    const level = Math.min(1, base * stagger);
    // scaleY for vertical bar scaling (transform: GPU-accelerated)
    return `scaleY(${Math.max(0.05, level)})`;
  }

  private async restartRecognition() {
    if (this.restartTimeout || !this.config.isListening) return;
    // Increase attempt counter and enforce a max
    this._restartAttempts++;
    if (this._restartAttempts > this.MAX_RESTART_ATTEMPTS) {
      console.warn('Too many recognition restart attempts, giving up for now.');
      // Give up and cleanup to avoid hot loops
      try { this.stopListening(); } catch (e) { /* ignore */ }
      return;
    }
    
    let delay;
    if (this._forceImmediateRestart) {
      delay = 200; // near-immediate restart
      this._forceImmediateRestart = false;
    } else {
      delay = Math.min(this.calculateRetryDelay(), 10000 * this._restartAttempts);
    }
    this.restartTimeout = setTimeout(async () => {
      try {
        await this.safeStopRecognition();
        const prev = (this.fullTranscript || '') + (this.interimTranscript || '');
        this.finalizeCurrentTranscript();
        this.detachAudioAnalyser();
        const oldRec = this.recognition;
        const newRec = this.createRecognition();
        let newStarted = false;
        try {
          newRec.start();
          newStarted = true;          
        } catch (startErr) {
          // start() may throw in some browsers; surface and let retry logic handle it
          console.warn('recognition.start() threw:', startErr);
          newStarted = false;
          throw startErr;
        }
        if (newStarted) {
          console.info('restartRecognition: overlapping start succeeded');

          // Swap to the new recognition instance
          this.recognition = newRec;
      
          // Stop the old recognition cleanly (if exists)
          if (oldRec) {
            try {
              oldRec.stop();
            } catch (e) {
              // ignore - best effort
            }
          }
        } else {
          console.info('restartRecognition: fallback stop-then-start');

          // Fallback: stop old, then create+start new (older approach)
          await this.safeStopRecognition();
          this.recognition = this.createRecognition();
          this.recognition.start();
        }

        if (this.mediaStream) {
          // reattach analyser and track listeners (safe to call even if already attached)
          this.attachAudioAnalyser(this.mediaStream);
          this.setupStreamTrackListeners(this.mediaStream);
        }

        this.fullTranscript = prev;
        this.interimTranscript = '';
        this.updateDisplayText();
        this.config.retryCount = 0;
        this._restartAttempts = 0;
      } catch (e) {
        // If safeStop or start failed, increment retryCount and possibly retry again
        console.warn('restartRecognition attempt failed', e);
        this.config.retryCount = (this.config.retryCount || 0) + 1;
        if (this.config.retryCount <= (this.config.maxRetries || 3)) {
          // schedule another restart using existing logic
          this.restartRecognition();
        } else {
          // Too many retries — stop listening to avoid infinite loop
          try { this.stopListening(); } catch (err) { /* ignore */ }
          this.alert.error('Unable to recover speech recognition. Please check microphone/connection.');
          // reset counter so future manual starts can try again
          this.config.retryCount = 0;
        }
      } finally {
         // Clear timeout handle
        if (this.restartTimeout) {
          clearTimeout(this.restartTimeout);
          this.restartTimeout = null;
        }
      }
    }, delay);
  }

  private calculateRetryDelay(): number {
    return Math.min(this.config.baseRetryDelay * Math.pow(2, this.config.retryCount), 30000);
  }

  private safeStopRecognition(): Promise<void> {
    return new Promise(resolve => {
      if (!this.recognition) return resolve();
      // Silence both onend and onerror so calling stop() here never triggers
      // handleRecognitionError (aborted) or handleRecognitionEnd (unwanted restart).
      const prevOnend   = this.recognition.onend;
      const prevOnerror = this.recognition.onerror;
      this.recognition.onend   = () => { /* noop */ };
      this.recognition.onerror = () => { /* noop */ };

      const timeout = setTimeout(() => {
        try { this.recognition.onend   = prevOnend;   } catch (e) {}
        try { this.recognition.onerror = prevOnerror; } catch (e) {}
        resolve();
      }, 1000);

      try {
        this.recognition.stop();
      } catch (e) {
        try { this.recognition.onend   = prevOnend;   } catch (ex) {}
        try { this.recognition.onerror = prevOnerror; } catch (ex) {}
        clearTimeout(timeout);
        resolve();
      }
    });
  }
  

  async checkAudioPermission(callback: (state: string) => void) {
    if (this.util.isiOS()) {
      callback('granted');
      return;
    }
  
    // Use the modern Permissions API if available (Chrome, Firefox, Edge)
    if (navigator.permissions && navigator.permissions.query) {
      try {
        const permissionStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        
        if (permissionStatus.state === 'granted') {
          callback('granted');
        } else if (permissionStatus.state === 'prompt') {
          // Status is 'prompt', so we now request permission, which will show the pop-up.
          this.requestPermissionFallback(callback); 
        } else {
          // Status is 'denied'.
          callback('denied');
        }
      } catch (e) {
        console.error("Permissions API query failed, falling back.", e);
        this.requestPermissionFallback(callback);
      }
    } 
    // Fallback for browsers without Permissions API (like Safari)
    else if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      this.requestPermissionFallback(callback);
    } else {
      console.error('Audio capture is not supported by this browser.');
      callback('denied');
    }
  }
  
  // Helper function to avoid repeating the getUserMedia call
  private requestPermissionFallback(callback: (state: string) => void) {
    navigator.mediaDevices.getUserMedia({ audio: true })
      .then(() => callback('granted'))
      .catch(error => {
        console.error('Error requesting audio permission:', error);
        callback('denied');
      });
  }

  onCloseDevicePopup(result: string) {
    this.config.showMicroPhonePopUp = false;
    if (result === 'granted') this.startListening();
    else this.alert.alert('Microphone access is required.');
  }

  get hasTranscript(): boolean {
    return !!this.fullTranscript.trim() || !!this.interimTranscript.trim();
  }

  // Sample AI Scan method
  public startScan() {
    this.alert.alert('AI Scan feature is under development.');
  }

  async close() {
    if (this.config.continousTranscription) {
      await this.sendDataToLLM(true);
    }
    if (this.isEmbeddedFloatingMode()) {
      this.resetAfterDone();
      return;
    }
    this.dialogRef.close({fullTranscript:this.fullTranscript.trim(),documentId:this.documentId});
  }
 

  createComponent(comp:ViewContainerRef,kxselector: any, input,callback) {
    setTimeout(() => {
      this.documentId = "";
      comp.clear();
      this.aiService.createComponent(comp,kxselector,input,(response)=>{
        if (response) { 
          if(response.instance.focus_out != undefined){
            response.instance.focus_out.subscribe((data)=>{
              this.onSaveFinished(data);
            });
          }
          if(response.instance.delete != undefined){          
            response.instance.delete.subscribe((data)=>{
              this.deleteImage();
            });
          }
          callback(response.instance)
        }

      })
    })
  }

  refreshScanUI(){
    var menuObj = {"actionType":{"action":"refresh","label":"field.label.refresh","class":"fa fa-refresh","permission_required":false},"projectionkeys":["prompt_code","tag_data","g_creation_time","id","care_type"]}
    this.config.uiEngineScanObj.buttonAction(menuObj);
  }

  deleteImage() {
    this.documentId = "";
  }

  onSaveFinished(event) {
    if(event.documentId != undefined){
      this.documentId = event.documentId;
    }else{
      this.documentId = "";
    }
  }

  ngOnDestroy() {
    this.stopListening();
    this.stopSTTRotation();
    this.destroyMediaRecorder();
    this.releaseMediaStream();
    if (this.audioUrl) {
      URL.revokeObjectURL(this.audioUrl);
      this.audioUrl = null;
    }
    if (this.audioContext) this.audioContext.close();
    if (this.restartTimeout) clearTimeout(this.restartTimeout);
    if (this.util.isSafari()) {
      document.removeEventListener('click', this.initSafariRecognition);
    }
    if(this.config.decorator){
      this.config.decorator.destroy()
    }
    this.subscriptions.unsubscribe();
  }

  async processData() {
    this.resetProgress('Preparing recorded data');
    this.config.async_task_id = false;
    this.config.loading = true;
    const hasData = (this.fullTranscript != undefined && this.fullTranscript != "")
      || (this.documentId != undefined && this.documentId != "")
      || !!this.recordedBlob
      || this.config.isListening; // still recording — stopping will produce a blob
    if (hasData) {
      this.stopListening(); // calls pauseMediaRecorder(), which sets _audioReadyPromise
      // Wait for the WAV conversion to finish before checking recordedBlob.
      // Without this await, recordedBlob is still null when we reach the branch below
      // because buildAudioUrl() is async (AudioContext.decodeAudioData).
      if (this._audioReadyPromise) {
        await this._audioReadyPromise;
        this._audioReadyPromise = null;
      }
      // Re-validate after stopping — blob may still be null if no audio was captured
      // (e.g. Done clicked immediately, or MediaRecorder silently failed)
      if (!this.fullTranscript && !this.recordedBlob && !this.documentId) {
        this.config.loading = false;
        this.alert.error('No audio was recorded. Please try again.');
        return;
      }
      if (this.config.continousTranscription) {
        // await final flush; show loader if needed
        await this.sendDataToLLM(true);
      } else if (this.recordedBlob) {
        // If the current blob was already uploaded, reuse its documentId to avoid
        // duplicate uploads when Done is clicked more than once.
        if (this.audioDocumentId) {
          this.getFillingData({ fullTranscript: this.fullTranscript, documentId: this.audioDocumentId });
        } else if (await this._saveVoiceRecording) {
          this.uploadAudioAndProcess();
        } else {
          this.processAudioInline();
        }
      } else {
        this.getFillingData({ fullTranscript: this.fullTranscript, documentId: this.documentId });
      }
    } else {
      this.alert.error("Please provide some data for form filling");
    }
  }

  private uploadAudioAndProcess(): void {
    const mimeToExt: Record<string, string> = {
      'audio/wav': 'wav', 'audio/webm': 'webm',
      'audio/mp4': 'mp4', 'audio/ogg': 'ogg'
    };
    const blobMime = (this.recordedBlob!.type || 'audio/wav').split(';')[0].trim();
    const ext = mimeToExt[blobMime] ?? 'wav';
    const fileName = `voice_recording_${Date.now()}.${ext}`;
    const fd = new FormData();
    fd.append('action', 'add');
    fd.append('fileName', fileName);
    fd.append('file', this.recordedBlob!, fileName);
    this.loader.showSpinner();
    const reader = new FileReader();
    reader.onloadend = () => {
      const fileContent = reader.result;
      this.baseService.upload(fd, 'dms', 'ai_scan', fileContent).subscribe(
        (res: any) => {
          this.loader.hideSpinner();
          if (res && res.errCode === 0 && res.documentId) {
            this.audioDocumentId = res.documentId;
            this.getFillingData({ fullTranscript: this.fullTranscript, documentId: res.documentId });
          } else {
            this.alertService.error(res?.message || 'Audio upload failed');
            this.config.loading = false;
          }
        },
        (err: any) => {
          this.loader.hideSpinner();
          this.config.loading = false;
          this.alertService.error('Audio upload failed');
        }
      );
    };
    reader.readAsDataURL(this.recordedBlob!);
  }

  /**
   * upload_document_voice = no: the recording goes to the LLM in the request
   * body and is never written to DMS, so there is no documentId to link.
   */
  private async processAudioInline(): Promise<void> {
    this.loader.showSpinner();
    try {
      const wav = (await this.toSpeechWav(this.recordedBlob!)) ?? this.recordedBlob!;
      const content = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(String(reader.result).split(',')[1] || '');
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(wav);
      });
      this.loader.hideSpinner();
      const content_type = (wav.type || 'audio/wav').split(';')[0].trim();
      this.getFillingData({ fullTranscript: this.fullTranscript, inlineAudio: { content, content_type } });
    } catch (e) {
      this.loader.hideSpinner();
      this.config.loading = false;
      this.alertService.error('Audio processing failed');
    }
  }

  /** Re-renders the recording as 16kHz mono WAV to keep the inline request small. */
  private async toSpeechWav(blob: Blob): Promise<Blob | null> {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      const OfflineCtx = window.OfflineAudioContext || (window as any).webkitOfflineAudioContext;
      if (!AudioCtx || !OfflineCtx) return null;
      const ctx = new AudioCtx();
      const decoded = await ctx.decodeAudioData(await blob.arrayBuffer());
      ctx.close();
      const rate = KxAISpeechComponent.INLINE_AUDIO_SAMPLE_RATE;
      const offline = new OfflineCtx(1, Math.ceil(decoded.duration * rate), rate);
      const src = offline.createBufferSource();
      src.buffer = decoded;
      src.connect(offline.destination);
      src.start();
      return this.encodeWav(await offline.startRendering());
    } catch (e) {
      console.warn('toSpeechWav failed, sending original recording', e);
      return null;
    }
  }

  private loadSaveVoiceRecordingConfig(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.baseService.get('GET_CONFIG', KxAISpeechComponent.SAVE_VOICE_CONFIG, 'facility_config_mapping', 'facility-management').subscribe(
        (res: any) => {
          if (res?.errCode !== 0) {
            resolve(false);
          } else if (res.resource?.length > 0 && res.resource[0]?.config_value) {
            resolve(String(res.resource[0].config_value).toLowerCase() !== 'no');
          } else {
            resolve(true); // not configured for this facility → config default "yes"
          }
        },
        () => resolve(false)
      );
    });
  }

  getSchema(callback){
    var schemaAvailable: any = this.dialogConfig.config?.resource_config?.ai?.form?.schema != undefined ? this.dialogConfig.config?.resource_config?.ai?.form?.schema : false;
          if (schemaAvailable) {
            this.baseService.getResourceDef(this.dialogConfig.config?.resource_config?.ai?.form?.schema, (schema) => {
              if (schema && Object.keys(schema).length > 0) {
                callback(schema)
              }else{
                callback(false)
              }              
            })
      } else {
        if(jQuery(".form_"+this.dialogConfig.config.resource).length){
          this.aiService.collectVisibleFields(jQuery(".form_"+this.dialogConfig.config.resource), {exclude:this.dialogConfig.data.formConfig.exclude},(schema)=>{
            if (schema && Object.keys(schema).length > 0) {
              callback(schema)
            }else{
              callback(false)
            }
          })
        }else{
          this.aiService.getFormSchema(this.dialogConfig.config.fieldGroup, this.dialogConfig.config?.resource_config).then((schema) => {
            if (schema && Object.keys(schema).length > 0) {
              callback(schema)
            }else{
              callback(false)
            }
          })
        }
        
      }
  }




  setDyanamicKey(formConfig,response,aiResponse){
    
    if(response.documentId != undefined && response.documentId != ""){
      var feature = AIService.SCAN_FILL;
      if(this.activeTab == 'voice'){
        feature = AIService.VOICE_FILL;
      }
      formConfig.feature = feature;
      aiResponse.feature = feature;
      aiResponse.dynamic_key = feature;
      this.config.dynamic_key = feature
      aiResponse.filter = "feature:"+feature+",documentId:"+response.documentId
    }else{
      formConfig.feature = AIService.VOICE_FILL;
      this.config.dynamic_key = AIService.VOICE_FILL
      aiResponse.feature = AIService.VOICE_FILL;
      aiResponse.dynamic_key = AIService.VOICE_FILL;
      aiResponse.filter = "feature:"+AIService.VOICE_FILL
    }
    if(this.activeTab == 'voice'){
      if(this.config.currentVoiceModeIsBackend){
        aiResponse.filter += ",voice_mode:true"
      }else{
        aiResponse.filter += ",voice_mode:false"
      }
    }
    
  }

  
  getFillingData(response){
   this.getSchema((schemaObj)=>{
    var data = this.dialogConfig?.data
    // if(this.config.loading){
    //   return true;
    // }
    var is_async = false;
    if(this.dialogConfig?.data.scanFillObj?.is_async){
      is_async = this.dialogConfig?.data.scanFillObj.is_async
    }else if(this.dialogConfig?.data.voiceFillObj?.is_async){
      is_async = this.dialogConfig?.data.voiceFillObj.is_async
    } 
    const transcriptLength = response?.fullTranscript?.length || 0;
    if (this.activeTab == 'voice' && transcriptLength > this.LARGE_TRANSCRIPT_ASYNC_THRESHOLD) {
      is_async = true;
    }
    // Inline audio is megabytes — too big for the async (Kafka) hop, so it always goes sync.
    if (response?.inlineAudio) {
      is_async = false;
    }
    this.loader.showSpinner();
    this.config.loading = true;
    this.startStatusRotation()
    var aiResponse:any = {};
    var formConfig = JSON.parse(JSON.stringify(data.formConfig))
    aiResponse.input = (schemaObj)?JSON.stringify(schemaObj):(data.schema != undefined)?JSON.stringify(data.schema):'';
    aiResponse.message = (response.fullTranscript != undefined)?this.appService.btoa_utf8(response.fullTranscript):'';
    aiResponse.model = (response.fullTranscript != undefined)?AIService.VOICE_FILL:AIService.SCAN_FILL;    
    this.setDyanamicKey(formConfig,response,aiResponse)
    // Forward the user's preference: send audio to LLM (true) vs use text transcript (false)
    aiResponse.use_audio_for_llm = this.config.currentVoiceModeIsBackend;
    if (response?.inlineAudio) {
      aiResponse.inline_audio = response.inlineAudio.content;
      aiResponse.inline_audio_type = response.inlineAudio.content_type;
    }

    if(this.config.decorator){
      var filter = (formConfig.filter != undefined)?aiResponse.filter+","+formConfig.filter:aiResponse.filter
      aiResponse.filter = this.config.decorator.processFilter(filter,this.dialogConfig)
    }
    var datasource:any = this.aiService.getDatasourceByAiConfig(this.config,formConfig);

    aiResponse.prompt_code = datasource.prompt_code;
    aiResponse.type = "is_async:"+((is_async)?is_async:false);
    datasource.headers = {"headers": new HttpHeaders()
    .set('timeout', String(this.AI_FILL_TIMEOUT_MS))}
    if(this.dialogConfig['aiConfig'])
        aiResponse.ai_config = this.appService.btoa_utf8(JSON.stringify(this.dialogConfig['aiConfig']))
    this.baseService.action(aiResponse,"AI_FILL",datasource.application,datasource.resource,undefined,datasource.headers).subscribe(
      (data) => {
        this.loader.hideSpinner();
        if (data.errCode == 0 && data.resource != undefined && data.resource.length>0) {
          if(is_async){
            const taskId = data.resource[0].id;
            this.config.async_task_id = taskId;
            // Poll every 5s (was 15s after a 20s wait) so progress written on the task shows up
            // live; retries are scaled so the overall timeout stays ~140s / ~620s as before.
            const asyncMaxRetries = transcriptLength > this.LARGE_TRANSCRIPT_ASYNC_THRESHOLD ? 124 : 28;
            setTimeout(()=>{
              this.aiService.retryCheckAsyncTask(
                taskId,
                (asyncRes) => {
                  this.config.loading = false;
                  if(asyncRes){
                    this.processFillData(
                      { resource: [asyncRes] },
                      formConfig
                    );
                  }else{
                    this.stopStatusRotation();
                  }
                },
                5000,
                asyncMaxRetries,
                // Only wire this live-progress callback when the flag is on, so processingStatus
                // is never overwritten by a backend-reported progress_message/current_step
                // while the plain cycling text is showing.
                this.showProgressBarEnabled()
                  // ignore a still-running poller from an earlier fill
                  ? (asyncRes) => { if (this.config.async_task_id === taskId) this.updateLiveProgress(asyncRes); }
                  : undefined
              );
            },3000)
            
          }else{
            this.processFillData(data,formConfig);
          }
          if(this.activeTab == 'scan'){
            this.config.uploadObj.removeWithoutDelete(0);
            this.deleteImage()
            this.refreshScanUI()
          }else{

          }
           
        }
        else {
          this.config.loading = false;
          this.loader.hideSpinner();
          this.stopStatusRotation();
          this.alertService.error(data.message)
        }
      },
      error=>{
        this.config.loading = false;
        this.loader.hideSpinner();
        this.stopStatusRotation();
        console.log(error);
        this.alertService.error(this.getAiErrorMessage(error))
      })
    })
  }

  private getAiErrorMessage(error: any): string {
    if (error?.name === 'TimeoutError') {
      return 'AI processing is taking longer than expected. Please try again or use a shorter note.';
    }
    return error?.message || String(error);
  }

  processFillData(data,formConfig){
    this.config.loading = false;
    this.stopStatusRotation(true);
    var message = {}
    if(data.resource[0].content != undefined){
      message = JSON.parse(data.resource[0].content)
    }else if(data.resource[0].message != undefined){
      message = JSON.parse(atob(data.resource[0].message))
    }
    if(this.config.decorator){
      message = this.config.decorator.beforeFilling(message,this.dialogConfig.config)
    }
    if(formConfig.selector != undefined){
      this.openCustomComponent(message,formConfig.selector)
    }else{
      let flatMessage = this.aiService.normalizeAiResponse(message,this.dialogConfig.config.formComponenet.form);
      this.fillData(flatMessage,this.dialogConfig.config.formComponenet.form);
    }
    this.sendData(message)
    
    if(this.config.decorator){
      this.config.decorator.afterFilling(message,this.dialogConfig.config)
    }
    if(formConfig.selector == undefined){
      if (this.isEmbeddedFloatingMode()) {
        this.resetAfterDone();
      } else {
        this.close();
      }
    } 
    this.config.manualMaximized = false  
  }

  private isEmbeddedFloatingMode(): boolean {
    return !!this.config?.minimized;
  }

  private resetAfterDone(): void {
    this.config.loading = false;
    this.config.manualMaximized = false;
    this.config.isTranscriptionExpanded = false;
    this.minimize();
    this.handleClearTranscription();
    this.cdr.detectChanges();
  }


   openCustomComponent(json,selector) {
   
    let popobj = {data:{json:json},size: DialogModalSize.Lg };
    //this.getOptions(this.data.schema,this.config?.formService?.questionMapping);
    this.config.popupModel = this.dialogService.openWithSelector(this.baseService,selector, popobj);
      this.config.popupModel["dialogRef"].afterClosed.subscribe(response => {
        if(response){
               
        }
        delete this.config.popupModel;
    });
  }

  //Per-control apply logic (setOptionValue/setRadioValue/setCheckboxGroupValue/parseAiDateValue/
  //parseAiCheckboxValue and the switch itself) lives in AIService.applyAiDataToTemplateForm()
  //now, shared with v1-prescription.component.ts - this method's own job is just resolving this
  //dialog's compositeFormMapping and preserving its notFound review-list bookkeeping.
  fillData(rawData: any, formGroup: any) {
    this.aiService.applyAiDataToTemplateForm(
      formGroup,
      rawData,
      this.config.formComponenet?.formService?.compositeFormMapping,
      (questionComponent: any, formControl: any) => {
        this.dialogConfig.data.notFound[questionComponent.question.key] = {questionComponent:questionComponent,control:formControl}
      }
    );
  }


  private setupSubscriptions(): void {
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
    this.subscriptions.add(
      this.transcriptionService.recordingStatus$.subscribe(isRecording => {
        this.config.isListening = isRecording;
        if (isRecording) {
          this.startDurationTimer();
        } else {
          this.stopDurationTimer();
        }
        this.updateRecordingUI();
      })
    );

    this.subscriptions.add(
      this.transcriptionService.transcriptionData$.subscribe(data => {
        this.handleTranscriptionData(data);
        this.updateRecordingUI();
      })
    );

    this.subscriptions.add(
      this.transcriptionService.error$.subscribe(error => {
        this.showToast('Transcription Error', error, 'destructive');
      })
    );

        // NEW: listen to system events (pause/resume/timeout) and handle auto-reconnect
        this.subscriptions.add(
          this.transcriptionService.systemEvents$.subscribe(evt => {
            if (!evt || !evt.type) return;
            switch (evt.type) {
              case 'recording_timed_out':
              // backend tells us the session ended due to long silence. Recreate session automatically.
              if (this._isAutoReconnecting) return;
              this._isAutoReconnecting = true;
              console.warn('recording_timed_out received — creating a fresh transcription session');
        
              // defensive cleanup
              try { this.transcriptionService.disconnect(); } catch (e) {}
              try { this.subscriptions.unsubscribe(); } catch (e) {}
        
              // small backoff to avoid hot loops
              setTimeout(() => {
                this.subscriptions = new Subscription(); // reset
                this.transcriptionService.connect((ok) => {
                  if (ok) {
                    this.setupSubscriptions(); // re-attach handlers
                    this.transcriptionService.startRecording?.();
                    this.config.isListening = true;
                    this._isAutoReconnecting = false;
                  } else {
                    console.warn('Silent reconnect failed');
                    this._isAutoReconnecting = false;
                  }
                });
              }, 500);
              break;

            case 'service_busy':
              this.alert.error('Server is busy. Please try again in a few seconds.');
              this.config.isListening = false;
              break;
            case 'session_stats':
              console.info('Session stats received', evt.data);
              break;
            }
          })
        );
  }

  async checkPermissions(retry = false): Promise<void> {
    try {
      // Query for permission status without prompting
      const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
      if (result.state === 'granted') {
          this.hasPermission = true;
      } else if (result.state === 'prompt' && retry) {
          // If retrying, now we prompt
          await navigator.mediaDevices.getUserMedia({ audio: true });
          this.hasPermission = true;
      } else {
          this.hasPermission = false;
      }
    } catch {
      this.hasPermission = false;
    }
    this.updateRecordingUI();
  }

  async handleStartRecording(): Promise<void> {
    if (!this.transcriptionService.isConnected) {
      this.showToast('Connection Error', 'Not connected to server. Please refresh.', 'destructive');
      return;
    }

    if (!this.hasPermission) {
      await this.checkPermissions(true); // Retry with prompt
      if (!this.hasPermission) {
        this.showToast('Permission Required', 'Microphone access is required.', 'destructive');
        return;
      }
    }

    this.fullTranscript = '';
    this.interimTranscript = '';
    this.config.wordCount = 0;
    this.transcriptionService.startRecording().then(() => {
        this.showToast('Recording Started', 'Using Google Medical Dictation for high-accuracy transcription.', 'success');
    }).catch(err => {
        // Error is already handled by the service's error$ observable
    });
  }

  handleStopRecording(): void {
    this.finalizeCurrentTranscript();
    this.transcriptionService.stopRecording();
    this.showToast('Recording Complete', 'Transcript finalized.', 'success');
  }
  
  private finalizeCurrentTranscript(): void {
    if (this.interimTranscript.trim()) {
      this.fullTranscript += this.interimTranscript + ' ';
      this.interimTranscript = '';
      this.updatewordCount();
    }
  }

  handleClearTranscription(): void {
    // Clear is clickable mid-recording too ([disabled] only gates the fully-idle
    // case) - stop first so isListening/isPaused/timers don't stay stuck on a
    // recording this reset is about to tear down underneath them.
    if (this.config.isListening) {
      this.stopListening();
    }
    this.fullTranscript = '';
    this.interimTranscript = '';
    this.config.wordCount = 0;
    this.config.lastSentOffset = 0;
    this._llmPending = false;
    this._llmQueue = [];
    this.audioDocumentId = '';
    // Fully destroy recorder + stream so next recording starts completely fresh
    this.destroyMediaRecorder();
    this.releaseMediaStream();
    this.audioChunks = [];
    this.recordedBlob = null;
    this.isPaused = false;
    this.totalRecordedMs = 0;
    this.recordingSessionStartMs = 0;
    this.config.duration = 0;
    this.maxRecordingReached = false;
    if (this.audioUrl) {
      URL.revokeObjectURL(this.audioUrl);
      this.audioUrl = null;
    }
    this.showToast('Transcription Cleared', 'All text has been cleared.', 'success');
  }

  async handleCopyTranscription(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.fullTranscript);
      this.showToast('Copied to clipboard', 'Transcription copied.', 'success');
    } catch (error) {
      this.showToast('Copy failed', 'Could not copy to clipboard.', 'destructive');
    }
  }

  handleExportTranscription(): void {
    if (!this.fullTranscript.trim()) {
      this.showToast('Nothing to Export', 'No text to export.', 'destructive');
      return;
    }
    const blob = new Blob([this.fullTranscript], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcription-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    this.showToast('Export Successful', 'Transcription downloaded.', 'success');
  }

  
  private handleTranscriptionData(data: TranscriptionData): void {
    this.updateTranscript(data.transcript, data.isFinal);
  }
  /**
   * Shared transcript update logic for both backend and browser.
   */
  private updateTranscript(transcript: string, isFinal: boolean): void {
    if (!transcript) return;
    if (isFinal) {
      this.fullTranscript += transcript.trim() + ' ';
      this.interimTranscript = '';
    } else {
      this.interimTranscript = transcript;
    }
    this.updatewordCount();
    this.scrollToBottom();
  }
  
  private updatewordCount(): void {
      const words = this.fullTranscript.trim().split(/\s+/).filter(word => word.length > 0);
      this.config.wordCount = words.length;
  }

  private scrollToBottom(): void {
    if (this.config.autoScroll) {
      setTimeout(() => {
        const container = document.getElementById('transcription-content');
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      }, 0);
    }
  }
  
  private updateRecordingUI(): void {
      // In Angular, the UI updates are handled reactively through data binding in the template.
      // We just need to ensure change detection runs.
      this.cdr.detectChanges();
  }

  // Timer Logic
  private startDurationTimer(): void {
    // Do NOT reset config.duration here — it accumulates across pause/resume cycles
    // so it reflects actual cumulative recording time, not wall-clock time.
    if (this.durationInterval) return; // already running
    this.durationInterval = window.setInterval(() => {
      this.config.duration++;
      // Auto-stop when the maximum recording limit is reached
      if (this.config.duration >= this.MAX_RECORDING_SECONDS) {
        this.maxRecordingReached = true;
        this.stopListening();
        this.alert.alert('Maximum recording time of 10 minutes reached. Press Done to submit or Clear to start over.');
      }
      this.updateRecordingUI();
    }, 1000);
  }

  private stopDurationTimer(): void {
    if (this.durationInterval) {
      clearInterval(this.durationInterval);
      this.durationInterval = undefined;
    }
  }

  formatDuration(): string {
    const minutes = Math.floor(this.config.duration / 60);
    const seconds = this.config.duration % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  // Toast Logic
  showToast(title: string, message: string, variant: any = 'default'): void {
    console.log(message)
    // const toastId = 'toast-' + Date.now();
    // this.toasts.push({ id: toastId, title, message, variant });

    // setTimeout(() => {
    //   this.removeToast(toastId);
    // }, 5000);
  }

  removeToast(id: string): void {
    //this.toasts = this.toasts.filter(toast => toast.id !== id);
    this.updateRecordingUI();
  }

  // New function to handle periodic data processing
  private startPeriodicProcessing(): void {
    if (!this.config.continousTranscription) return;
    this.stopPeriodicProcessing();
    const intervalMs = Math.max(1000, (this.config.intervalTranscription || 30) * 1000);
    this.config.transcriptionTimer = window.setInterval(() => {
      // Snapshot any pending interim speech into fullTranscript so it's
      // visible to getNewTranscriptSinceLastSend — without this, interim
      // results only reach fullTranscript at the 25 s STT rotation, causing
      // the first LLM send to be delayed to ~30-35 s even with a 10 s interval.
      this.finalizeCurrentTranscript();
      if (this._llmInFlight) {
        this._llmPending = true; // coalesce until in-flight finishes
        return;
      }
      this.sendDataToLLM(false);
    }, intervalMs);
  }

// Function to stop the timer
private stopPeriodicProcessing(): void {
  if (this.config.transcriptionTimer) {
      clearInterval(this.config.transcriptionTimer);
      this.config.transcriptionTimer = null;
  }
}

// Function to send data to the LLM
private async sendDataToLLM(isDone = false): Promise<void> {
  const newTranscript = this.getNewTranscriptSinceLastSend(isDone).trim();
  // This prevents sending every small word or correction during dictation.
  const wordCount = newTranscript.split(/\s+/).filter(word => word.length > 0).length;
  if (!isDone && newTranscript.length > 0 && wordCount < this.MIN_WORDS_THRESHOLD) {
    console.log(`Transcript below word threshold (${wordCount}/${this.MIN_WORDS_THRESHOLD}). Waiting for more input.`);
    return;
  }

  if (!newTranscript) {
    console.log('No new transcript to process.');
    return;
  }

  // If an LLM send is already in flight, mark pending and return (coalesce)
  if (this._llmInFlight) {
    this._llmPending = true;
    return;
  }

  // Choose a chunk (simple char-based chunking; you can later switch to token-based)
  const toSend = newTranscript.length > this.MAX_CHUNK_CHARS
    ? newTranscript.slice(0, this.MAX_CHUNK_CHARS)
    : newTranscript;

  this._llmInFlight = true;
  let attempt = 0;
  let success = false;

  // Prepare common datasource once
    const data = this.dialogConfig?.data;
    const formConfig = JSON.parse(JSON.stringify(data?.formConfig || {}));

    var aiResponse:any = {};
    aiResponse.input = btoa((data.schema != undefined)?JSON.stringify(data.schema):'');
   
   
    formConfig.feature = AIService.VOICE_FILL;
    aiResponse.feature = AIService.VOICE_FILL;
    aiResponse.filter = "feature:"+AIService.VOICE_FILL+",previous_response:"+btoa(JSON.stringify(this.config?.previousResponses || []));
    
    var datasource:any = this.aiService.getDatasourceByAiConfig(this.config,formConfig);

    aiResponse.prompt_code = datasource.prompt_code;
    datasource.headers = { headers: new HttpHeaders().set('timeout', String(this.AI_FILL_TIMEOUT_MS)) };

  while (attempt <= this.LLM_MAX_RETRIES && !success) {
    try {
      attempt++;
      aiResponse.message = this.appService.btoa_utf8(toSend);
      console.info('LLM send start', { offset: this.config.lastSentOffset, length: toSend.length },aiResponse);
      if(this.dialogConfig['aiConfig'])
        aiResponse.aiConfig = this.dialogConfig['aiConfig']
      this.config.loading=true;
      // Convert Observable -> Promise
      const response = await firstValueFrom(
        this.baseService.action(aiResponse, 'AI_FILL', datasource.application, datasource.resource, undefined, datasource.headers)
      );
      this.config.loading=false
      if (response && response.errCode === 0) {
        // success: update previousResponses if applicable
        if (response.resource && response.resource.length) {
          console.info('LLM send success', { newOffset: this.config.lastSentOffset });
          var message = {};
          try{
            message = JSON.parse(atob(response.resource[0].message))
            this.sendData(message)
          }catch(e){
            console.error(e)
          }
          this.config.previousResponses = (this.config.previousResponses || []).concat(message);
        }
        this.updateProcessedTranscriptState(toSend);
        success = true;
      } else {
        throw new Error(response?.message || 'LLM returned error');
      }
    } catch (err) {
      console.warn(`LLM send attempt ${attempt} failed`, err);
      if (attempt > this.LLM_MAX_RETRIES) {
        this.showToast('LLM Error', 'Could not send transcript after multiple attempts', 'destructive');
      } else {
        const delay = Math.min(this.LLM_BASE_DELAY * Math.pow(2, attempt - 1), 30000);
        await new Promise(res => setTimeout(res, delay));
      }
    }
  }

  this._llmInFlight = false;

  // If new transcript arrived while we were sending, trigger another send soon (coalesce)
  if (this._llmPending) {
    this._llmPending = false;
    setTimeout(() => this.sendDataToLLM(false), 200);
  }
}

sendData(data) {
  // Attach the document ID that was used for this fill:
  // audioDocumentId for voice recordings, documentId for AI Scan uploads.
  const ai_document_id = this.audioDocumentId || this.documentId || '';
  const payload = { ...data, ai_document_id };
  console.log("FINAL_DATA===>", payload)
  this.getData.next(payload);
}



private getNewTranscriptSinceLastSend(isDone = false): string {
  if (isDone) {
    this.finalizeCurrentTranscript();
  }
  const currentFullTranscript = this.fullTranscript || '';
  const lastOffset = this.config?.lastSentOffset || 0;
  if (lastOffset >= currentFullTranscript.length) return '';
  return currentFullTranscript.substring(lastOffset);
}


// Helper function to update the state after a successful send
private updateProcessedTranscriptState(sentTranscript: string): void {
  const old = this.config?.lastSentOffset || 0;
  this.config.lastSentOffset = old + (sentTranscript ? sentTranscript.length : 0);
}

private attachAudioAnalyser(stream: MediaStream) {
  try {
    if (!stream) return;
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }

    // ensure we detach any existing analyser first
    this.detachAudioAnalyser();

    this.audioSourceNode = this.audioContext.createMediaStreamSource(stream);
    this.analyserNode = this.audioContext.createAnalyser();
    this.analyserNode.fftSize = 1024;
    this.audioSourceNode.connect(this.analyserNode);

    const buffer = new Float32Array(this.analyserNode.fftSize);

    const poll = () => {
      try {
        if (!this.analyserNode) return;
        this.analyserNode.getFloatTimeDomainData(buffer);
        let sum = 0;
        for (let i = 0; i < buffer.length; i++) sum += buffer[i] * buffer[i];
        const rms = Math.sqrt(sum / buffer.length);
        if (rms > this.AUDIO_ENERGY_THRESHOLD) {
          //console.debug('VAD rms', rms, 'updating lastAudioTimestamp');
          this.config.lastAudioTimestamp = Date.now();
          const scale = this.AUDIO_ENERGY_THRESHOLD * 8; // tune this multiplier
          const level = Math.min(1, rms / Math.max(1e-7, scale));
          this.micLevel = parseFloat(level.toFixed(3));
        } else {
          // slowly decay microphone level when quiet
          this.micLevel = Math.max(0, parseFloat((this.micLevel * 0.92).toFixed(3)));
        }
      } catch (e) {
        // analyser may throw if audioContext closed; swallow and stop poll
      }

      // schedule next poll while listening
      if (this.config.isListening && this.analyserNode) {
        this.analyserPollTimer = setTimeout(poll, 120);
      }
    };

    // seed lastAudioTimestamp so short sessions don't immediately appear stale
    this.config.lastAudioTimestamp = Date.now();
    poll();
  } catch (e) {
    console.warn('attachAudioAnalyser failed', e);
  }
}

private detachAudioAnalyser() {
  try {
    if (this.analyserPollTimer) {
      clearTimeout(this.analyserPollTimer);
      this.analyserPollTimer = null;
    }
    if (this.audioSourceNode) {
      try { this.audioSourceNode.disconnect(); } catch (e) {}
      this.audioSourceNode = null;
    }
    if (this.analyserNode) {
      try { this.analyserNode.disconnect(); } catch (e) {}
      this.analyserNode = null;
    }
    // Do not close audioContext here — you already close it in ngOnDestroy
  } catch (e) {
    console.warn('detachAudioAnalyser failed', e);
  }
}
private setupStreamTrackListeners(stream: MediaStream) {
  if (!stream) return;
  stream.getAudioTracks().forEach(track => {
    // attach short handlers; replacement during cleanup simply nulls these
    track.onended = () => {
      console.warn('Audio track ended');
      // Only attempt STT restart when speech recognition is available;
      // in Firefox / mobile WebViews there is nothing to restart.
      if (this.hasBrowserSpeechRecognition) this.restartRecognition();
    };
    track.onmute = () => {
      console.warn('Audio track muted');
      // delay a bit to avoid flapping — only restart if it's genuinely non-live
      setTimeout(() => {
        if (this.hasBrowserSpeechRecognition && (!track.enabled || track.readyState !== 'live')) {
          this.restartRecognition();
        }
      }, 800);
    };
    track.onunmute = () => {
      console.log('Audio track unmuted');
      this.config.lastAudioTimestamp = Date.now();
    };
  });
}

// Re-acquire media stream if the existing one is missing or tracks are dead.
// Returns true if a usable stream is available after this call.
private async reAcquireMediaStream(): Promise<boolean> {
  try {
    const tracks = this.mediaStream?.getAudioTracks() || [];
    const micLive = tracks.length > 0 && tracks.some(t => t.readyState === 'live' && t.enabled);
    if (micLive) return true;

    // Try to get user media again (will prompt if permission not persistent)
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    if (stream) {
      // cleanup previous stream if any
      try {
        this.mediaStream?.getTracks().forEach(t => { try { t.stop(); } catch(e) {} });
      } catch (e) {}
      this.mediaStream = stream;
      // reattach analyser and track listeners
      this.attachAudioAnalyser(this.mediaStream);
      this.setupStreamTrackListeners(this.mediaStream);
      return true;
    }
    return false;
  } catch (e) {
    console.warn('reAcquireMediaStream failed', e);
    return false;
  }
}

// ====== Continuous MediaRecorder ======

  /**
   * Starts or resumes the single long-lived MediaRecorder.
   * - First call: creates the recorder and calls start().
   * - Subsequent calls after a user-stop: just calls resume() on the same instance.
   * This guarantees all audio lives in ONE continuous WebM container, so the blob
   * has valid metadata and Gemini / any consumer can parse it correctly.
   */
  private startMediaRecorder(stream: MediaStream): void {
    if (this.mediaRecorder && this.mediaRecorder.state === 'paused') {
      try { this.mediaRecorder.resume(); } catch (e) { console.warn('MediaRecorder resume failed', e); }
      return;
    }
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') return; // already live

    // First start — create a fresh recorder
    const options: MediaRecorderOptions = {};
    if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
      options.mimeType = 'audio/webm;codecs=opus';
    } else if (MediaRecorder.isTypeSupported('audio/webm')) {
      options.mimeType = 'audio/webm';
    } else if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
      options.mimeType = 'audio/ogg;codecs=opus';
    } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
      options.mimeType = 'audio/mp4'; // iOS Safari
    }
    try {
      this.mediaRecorder = new MediaRecorder(stream, options);
      this.mediaRecorderMimeType = this.mediaRecorder.mimeType || 'audio/webm';
      this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };
      this.mediaRecorder.start(1000); // 1 s timeslice — flushes chunks regularly
    } catch (e) {
      console.warn('MediaRecorder start failed', e);
      this.mediaRecorder = null;
    }
  }

  /**
   * Flushes the current chunk then pauses the recorder (does NOT stop it).
   * After the pending ondataavailable fires, rebuilds the audio URL from all
   * accumulated chunks so the player shows the full recording so far.
   */
  private pauseMediaRecorder(): void {
    if (!this.mediaRecorder || this.mediaRecorder.state !== 'recording') return;
    // Build the URL only after the recorder confirms it has flushed all buffered data.
    // The 'pause' event fires after the last ondataavailable for this segment, so we
    // are guaranteed all chunks are in audioChunks[] by the time buildAudioUrl runs.
    // Store the promise so processData() can await it before checking recordedBlob.
    this._audioReadyPromise = new Promise<void>(resolve => {
      this.mediaRecorder!.addEventListener('pause', async () => {
        await this.buildAudioUrl();
        resolve();
      }, { once: true });
    });
    try {
      this.mediaRecorder.requestData(); // flush whatever is still buffered
      this.mediaRecorder.pause();
    } catch (e) {
      console.warn('MediaRecorder pause failed', e);
      this._audioReadyPromise = Promise.resolve(); // don't leave callers hanging
    }
  }

  /** Builds (or rebuilds) the playable URL from all accumulated chunks. */
  private async buildAudioUrl(): Promise<void> {
    if (!this.audioChunks.length) return;
    if (this.audioUrl) URL.revokeObjectURL(this.audioUrl);
    const rawMime = this.mediaRecorderMimeType.split(';')[0].trim();
    const raw = new Blob(this.audioChunks, { type: rawMime });
    // Convert to WAV (PCM-16) — the standard Gemini API only accepts
    // audio/wav, audio/mp3, audio/aiff, audio/aac, audio/ogg, audio/flac.
    // audio/webm (Chrome's native MediaRecorder format) is NOT on that list.
    const wav = await this.convertToWav(raw);
    this.recordedBlob = wav ?? raw;
    this.audioUrl = URL.createObjectURL(this.recordedBlob);
    this.cdr.detectChanges();
  }

  /**
   * Decodes the recorded blob via AudioContext and re-encodes as PCM-16 WAV.
   * Returns null if decoding fails (caller falls back to the original blob).
   */
  private async convertToWav(blob: Blob): Promise<Blob | null> {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return null;
      const ctx = new AudioCtx();
      const audioBuffer = await ctx.decodeAudioData(await blob.arrayBuffer());
      ctx.close();
      return this.encodeWav(audioBuffer);
    } catch (e) {
      console.warn('convertToWav failed, keeping original blob', e);
      return null;
    }
  }

  /** Encodes an AudioBuffer to a PCM-16 LE WAV Blob. */
  private encodeWav(buffer: AudioBuffer): Blob {
    const numCh = buffer.numberOfChannels;
    const sr    = buffer.sampleRate;
    const len   = buffer.length;
    const bps   = 2; // 16-bit
    const dataBytes = len * numCh * bps;
    const wav = new ArrayBuffer(44 + dataBytes);
    const v   = new DataView(wav);
    const str = (off: number, s: string) => { for (let i = 0; i < s.length; i++) v.setUint8(off + i, s.charCodeAt(i)); };
    str(0, 'RIFF'); v.setUint32(4, 36 + dataBytes, true);
    str(8, 'WAVE'); str(12, 'fmt ');
    v.setUint32(16, 16, true);  v.setUint16(20, 1, true);       // PCM
    v.setUint16(22, numCh, true); v.setUint32(24, sr, true);
    v.setUint32(28, sr * numCh * bps, true); v.setUint16(32, numCh * bps, true); v.setUint16(34, 16, true);
    str(36, 'data'); v.setUint32(40, dataBytes, true);
    let off = 44;
    for (let i = 0; i < len; i++) {
      for (let c = 0; c < numCh; c++) {
        const s = buffer.getChannelData(c)[i];
        v.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
        off += 2;
      }
    }
    return new Blob([wav], { type: 'audio/wav' });
  }

  /**
   * Fully destroys the MediaRecorder (called on clear and ngOnDestroy only).
   * After this, the next startListening will create a brand-new recorder.
   */
  private destroyMediaRecorder(): void {
    if (!this.mediaRecorder) return;
    const recorder = this.mediaRecorder;
    this.mediaRecorder = null;
    this._audioReadyPromise = null;
    recorder.ondataavailable = null;
    if (recorder.state !== 'inactive') {
      try { recorder.stop(); } catch (e) {}
    }
  }

  /**
   * Injects or patches the WebM Duration element so Gemini (and any other
   * consumer) sees the correct duration instead of rejecting the file.
   *
   * Chrome's MediaRecorder NEVER writes a Duration element — ffprobe confirms
   * the field is simply absent. We must INSERT it into the SegmentInfo block.
   *
   * Strategy:
   *  1. Scan the first 32 KB for SegmentInfo ID (0x15 0x49 0xA9 0x66).
   *  2. Inside SegmentInfo, check whether Duration (0x44 0x89) already exists.
   *     - If yes and value is wrong: overwrite in-place.
   *     - If no: insert an 11-byte Duration element at the start of the block
   *       and update the SegmentInfo size VINT accordingly.
   *
   * EBML Duration element layout (11 bytes total):
   *   0x44 0x89           — element ID (2 bytes)
   *   0x88                — VINT size = 8 (1 byte)
   *   <float64 big-endian> — value in ms (8 bytes)
   */
  private async patchWebMDuration(blob: Blob, durationMs: number): Promise<Blob> {
    console.log('[WebM patch] called — durationMs:', durationMs, 'blobSize:', blob.size, 'mimeType:', blob.type);
    if (!durationMs || durationMs <= 0) {
      console.warn('[WebM patch] skipped — durationMs is 0 or missing');
      return blob;
    }
    try {
      const buffer = await blob.arrayBuffer();
      const bytes  = new Uint8Array(buffer);
      const view   = new DataView(buffer);

      // --- EBML helpers ---
      const readVint = (offset: number): { value: number; len: number } => {
        const b = bytes[offset];
        if (!b) return { value: -1, len: 1 };
        let len = 1, mask = 0x80;
        while (!(b & mask) && len <= 8) { len++; mask >>= 1; }
        let value = b & (mask - 1);
        for (let k = 1; k < len; k++) value = value * 256 + bytes[offset + k];
        return { value, len };
      };

      const writeVint = (buf: Uint8Array, offset: number, value: number, len: number): void => {
        const marker = 0x80 >> (len - 1);
        buf[offset] = marker | (value / Math.pow(256, len - 1)) | 0;
        for (let k = 1; k < len; k++) {
          buf[offset + k] = Math.floor(value / Math.pow(256, len - 1 - k)) & 0xFF;
        }
      };

      // Duration element bytes (11 bytes): ID + VINT(8) + Float64
      const makeDurationElement = (ms: number): Uint8Array => {
        const el = new Uint8Array(11);
        el[0] = 0x44; el[1] = 0x89; // Duration ID
        el[2] = 0x88;                // VINT size = 8
        new DataView(el.buffer).setFloat64(3, ms, false); // big-endian
        return el;
      };

      // Search only the first 32 KB — SegmentInfo is always near the top
      const scanLimit = Math.min(bytes.length, 32768);

      for (let i = 0; i < scanLimit - 4; i++) {
        // SegmentInfo ID = 0x15 0x49 0xA9 0x66
        if (bytes[i] !== 0x15 || bytes[i+1] !== 0x49 || bytes[i+2] !== 0xA9 || bytes[i+3] !== 0x66) continue;

        const sizeVint    = readVint(i + 4);
        const contentStart = i + 4 + sizeVint.len;
        console.log(`[WebM patch] SegmentInfo found at 0x${i.toString(16)}, size=${sizeVint.value}, sizeLen=${sizeVint.len}, contentStart=0x${contentStart.toString(16)}`);

        // "Unknown size" marker — can't reliably insert, bail
        const UNKNOWN_SIZE = Math.pow(2, 7 * sizeVint.len) - 1;
        if (sizeVint.value >= UNKNOWN_SIZE) {
          console.warn('[WebM patch] SegmentInfo has unknown size, cannot insert');
          break;
        }

        const contentEnd = contentStart + sizeVint.value;
        if (contentEnd > bytes.length) break;

        // --- Case A: Duration element already present, just overwrite ---
        for (let j = contentStart; j < contentEnd - 2; j++) {
          if (bytes[j] !== 0x44 || bytes[j+1] !== 0x89) continue;
          const sb = bytes[j + 2];
          let szLen: number;
          if      (sb === 0x88)                       szLen = 1;
          else if (sb === 0x40 && bytes[j+3] === 0x08) szLen = 2;
          else continue;
          const valOff = j + 2 + szLen;
          const cur    = view.getFloat64(valOff, false);
          console.log(`[WebM patch] Case A — Duration element found at 0x${j.toString(16)}, current value=${cur}, writing ${durationMs}`);
          if (cur > 0 && isFinite(cur)) { console.log('[WebM patch] already valid, no change'); return blob; }
          const patched = new Uint8Array(buffer.slice(0));
          new DataView(patched.buffer).setFloat64(valOff, durationMs, false);
          console.log('[WebM patch] Case A done — overwrote existing Duration');
          return new Blob([patched], { type: blob.type });
        }

        // --- Case B: Duration missing — INSERT at start of SegmentInfo content ---
        console.log('[WebM patch] Case B — Duration element absent, inserting');
        const newContentSize = sizeVint.value + 11;
        const maxForLen = Math.pow(2, 7 * sizeVint.len) - 1;
        if (newContentSize > maxForLen) {
          console.warn('[WebM patch] SegmentInfo size overflow (need larger VINT), skipping');
          break;
        }

        const sizeOffset = i + 4;
        const durationEl = makeDurationElement(durationMs);
        const newBuf     = new Uint8Array(bytes.length + 11);

        let pos = 0;
        // 1. Everything up to the SegmentInfo size VINT
        newBuf.set(bytes.subarray(0, sizeOffset), pos); pos += sizeOffset;
        // 2. Updated size VINT (same byte-length, bigger value)
        writeVint(newBuf, pos, newContentSize, sizeVint.len);   pos += sizeVint.len;
        // 3. Injected Duration element
        newBuf.set(durationEl, pos);                            pos += 11;
        // 4. Original SegmentInfo content
        newBuf.set(bytes.subarray(contentStart, contentEnd), pos); pos += sizeVint.value;
        // 5. Everything after SegmentInfo
        newBuf.set(bytes.subarray(contentEnd), pos);

        return new Blob([newBuf], { type: blob.type });
      }
    } catch (e) {
      console.warn('patchWebMDuration failed, returning original blob', e);
    }
    return blob;
  }

  // ====== Predictable STT rotation ======

  /**
   * Schedules a clean SpeechRecognition restart every STT_ROTATION_MS (25 s).
   * We kill it ourselves instead of letting the browser kill it randomly.
   */
  private startSTTRotation(): void {
    this.stopSTTRotation();
    this.sttRestartInterval = setInterval(() => {
      if (!this.config.isListening || this.config.isManualStop) {
        this.stopSTTRotation();
        return;
      }
      this.rotateSpeechRecognition();
    }, this.STT_ROTATION_MS);
  }

  private stopSTTRotation(): void {
    if (this.sttRestartInterval) {
      clearInterval(this.sttRestartInterval);
      this.sttRestartInterval = null;
    }
  }

  /**
   * Performs one planned 25 s STT rotation with zero perceptible gap.
   *
   * Chrome/Edge only allow a single live SpeechRecognition at a time, so we
   * CANNOT overlap-start a new instance while the old one is still running —
   * that would fire onerror → handleRecognitionError → stopListening (breaking
   * the whole session).
   *
   * Instead we:
   *   1. Commit any pending interim text (no word lost).
   *   2. Neutralise the old instance (silence onend/onresult/onerror).
   *   3. Hijack the old instance's onend to immediately start the new one.
   *   4. Call oldRec.stop() — browser fires onend within ~50 ms, we start new.
   */
  private rotateSpeechRecognition(): void {
    if (!this.config.isListening || this.config.isManualStop) return;

    // Abort any pending error-recovery restart — rotation takes precedence
    if (this.restartTimeout) {
      clearTimeout(this.restartTimeout);
      this.restartTimeout = null;
    }

    // Save interim before the instance switch — nothing is lost
    this.finalizeCurrentTranscript();

    const oldRec = this.recognition;
    if (!oldRec) return;

    // Neutralise the old instance completely
    oldRec.onresult = () => {};
    oldRec.onerror  = () => {};

    // Chain: the moment old fires onend, start the new instance immediately
    oldRec.onend = () => {
      if (!this.config.isListening || this.config.isManualStop) return;
      try {
        const newRec = this.createRecognition();
        newRec.start();
        this.recognition = newRec;
        this._restartAttempts = 0;
        this.config.retryCount = 0;
        this.config.lastResultTimestamp = Date.now();
      } catch (e) {
        console.error('STT rotation: new instance start failed', e);
      }
    };

    try { oldRec.stop(); } catch (e) { console.warn('STT rotation: old stop failed', e); }
  }

onTranscriptChange(event: Event) {
  this.fullTranscript = (event.target as HTMLElement).innerText;
}

/**
 * MediaRecorder-generated WebM blobs lack duration metadata, so the browser
 * reports duration=Infinity and renders the seek bar as "full / at end".
 * This forces a seek to a huge timestamp which makes the browser scan for the
 * real end-of-stream, then rewinds to 0 so playback starts from the beginning.
 */
fixAudioDuration(event: Event): void {
  const audio = event.target as HTMLAudioElement;
  if (!isFinite(audio.duration)) {
    audio.currentTime = 1e101;
    audio.addEventListener('timeupdate', () => { audio.currentTime = 0; }, { once: true });
  }
}

}